import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, MapPin, Globe, ArrowLeft, Loader2 } from 'lucide-react';
import { PackageCard } from '../components/PackageCard';
import { FiltersPanel } from '../components/FiltersPanel';
import { getPackages } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default images for continents/countries without custom images
const DEFAULT_IMAGES = {
  continents: {
    'Europa': 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800',
    'Asia': 'https://images.unsplash.com/photo-1535139262971-c51845b16e6c?w=800',
    'Africa': 'https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?w=800',
    'America de Nord': 'https://images.unsplash.com/photo-1534430480872-3498386e7856?w=800',
    'America de Sud': 'https://images.unsplash.com/photo-1619546952812-520e98064a52?w=800',
    'Oceania': 'https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=800',
    'default': 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=800'
  },
  countries: {
    'Italia': 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=600',
    'Franța': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600',
    'Spania': 'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=600',
    'Grecia': 'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=600',
    'Germania': 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=600',
    'Turcia': 'https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?w=600',
    'Egipt': 'https://images.unsplash.com/photo-1539768942893-daf53e448371?w=600',
    'Maroc': 'https://images.unsplash.com/photo-1489749798305-4fea3ae63d43?w=600',
    'Thailanda': 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=600',
    'Japonia': 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=600',
    'China': 'https://images.unsplash.com/photo-1508804185872-d7badad00f7d?w=600',
    'default': 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=600'
  }
};

// Helper to create URL-safe slug
const createSlug = (name) => {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

// Continent card component
const ContinentCard = ({ continent }) => {
  const slug = createSlug(continent.name);
  const image = continent.image_url || DEFAULT_IMAGES.continents[continent.name] || DEFAULT_IMAGES.continents.default;
  
  return (
    <Link
      to={`/circuite/${slug}`}
      className="group block relative h-64 md:h-72 rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]"
      data-testid={`continent-card-${slug}`}
    >
      <img
        src={image}
        alt={continent.name}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent z-10" />
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-end p-6 text-center">
        <Globe className="w-10 h-10 text-gold mb-3 group-hover:scale-110 transition-transform" />
        <h3 className="font-heading font-bold text-2xl text-white mb-2">{continent.name}</h3>
        <p className="text-white/70 text-sm">{continent.description || `Circuite prin ${continent.name}`}</p>
        <div className="mt-3 flex items-center gap-1 text-gold text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          <span>Explorează</span>
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
};

// Country card component
const CountryCard = ({ country, continentSlug }) => {
  const slug = createSlug(country.name);
  const image = country.image_url || DEFAULT_IMAGES.countries[country.name] || DEFAULT_IMAGES.countries.default;
  
  return (
    <Link
      to={`/circuite/${continentSlug}/${slug}`}
      className="group block relative h-48 md:h-56 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_25px_rgba(212,175,55,0.3)]"
      data-testid={`country-card-${slug}`}
    >
      <img
        src={image}
        alt={country.name}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent z-10" />
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-end p-4 text-center">
        <MapPin className="w-6 h-6 text-gold mb-2" />
        <h4 className="font-heading font-semibold text-lg text-white">{country.name}</h4>
        <div className="mt-2 flex items-center gap-1 text-gold text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          <span>Vezi circuite</span>
          <ChevronRight className="w-3 h-3" />
        </div>
      </div>
    </Link>
  );
};

export default function CircuitePage() {
  const { continent, country } = useParams();
  
  // Data from API
  const [continents, setContinents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingGeo, setLoadingGeo] = useState(true);
  
  // Package data
  const [packages, setPackages] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: null, max: null });
  const [selectedTransport, setSelectedTransport] = useState([]);
  const [sortOrder, setSortOrder] = useState('none');
  const [loading, setLoading] = useState(false);

  // Helper function to parse price
  const parsePrice = (price) => {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      const match = price.replace(/\./g, '').match(/[\d]+/);
      return match ? parseInt(match[0]) : 0;
    }
    return 0;
  };

  // Load continents and countries from API
  useEffect(() => {
    const loadGeoData = async () => {
      setLoadingGeo(true);
      try {
        const [continentsRes, countriesRes] = await Promise.all([
          fetch(`${API_URL}/api/continents`),
          fetch(`${API_URL}/api/countries?category=Circuite%20Externe`)
        ]);
        
        const continentsData = await continentsRes.json();
        const countriesData = await countriesRes.json();
        
        setContinents(continentsData);
        setCountries(countriesData);
      } catch (error) {
        console.error('Error loading geo data:', error);
      } finally {
        setLoadingGeo(false);
      }
    };
    
    loadGeoData();
  }, []);

  // Find current continent and country based on URL params
  const currentContinent = useMemo(() => {
    if (!continent) return null;
    return continents.find(c => createSlug(c.name) === continent);
  }, [continent, continents]);

  const currentCountry = useMemo(() => {
    if (!country || !currentContinent) return null;
    return countries.find(c => 
      c.continent_id === currentContinent.id && 
      createSlug(c.name) === country
    );
  }, [country, currentContinent, countries]);

  // Get countries for current continent
  const continentCountries = useMemo(() => {
    if (!currentContinent) return [];
    return countries.filter(c => c.continent_id === currentContinent.id);
  }, [currentContinent, countries]);

  // Load packages when viewing a specific country
  useEffect(() => {
    if (currentCountry) {
      const loadPackages = async () => {
        setLoading(true);
        try {
          const pkgs = await getPackages({ category: 'Circuite Externe' });
          
          // Filter by country name
          const countryName = currentCountry.name.toLowerCase();
          const filtered = pkgs.filter(pkg => {
            const pkgCountry = (pkg.country || '').toLowerCase();
            const pkgTitle = (pkg.title || '').toLowerCase();
            const pkgDesc = (pkg.short_description || '').toLowerCase();
            
            return pkgCountry === countryName ||
                   pkgTitle.includes(countryName) ||
                   pkgDesc.includes(countryName);
          });
          
          setPackages(filtered);
          
          // Calculate operators from filtered packages (not global operators)
          const operatorCounts = {};
          filtered.forEach(pkg => {
            if (pkg.operator) {
              operatorCounts[pkg.operator] = (operatorCounts[pkg.operator] || 0) + 1;
            }
          });
          
          // Convert to array format and filter only operators with packages
          const countryOperators = Object.entries(operatorCounts)
            .filter(([_, count]) => count > 0)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count);
          
          setOperators(countryOperators);
        } catch (error) {
          console.error('Error loading packages:', error);
        } finally {
          setLoading(false);
        }
      };
      
      loadPackages();
    } else {
      setPackages([]);
    }
  }, [currentCountry]);

  // Apply filters
  const filteredPackages = useMemo(() => {
    let result = [...packages];
    
    // Filter by operators
    if (selectedOperators.length > 0) {
      result = result.filter(pkg => selectedOperators.includes(pkg.operator));
    }
    
    // Filter by price range
    if (priceRange.min !== null) {
      result = result.filter(pkg => parsePrice(pkg.price) >= priceRange.min);
    }
    if (priceRange.max !== null) {
      result = result.filter(pkg => parsePrice(pkg.price) <= priceRange.max);
    }
    
    // Filter by transport
    if (selectedTransport.length > 0) {
      result = result.filter(pkg => selectedTransport.includes(pkg.transport_type || 'avion'));
    }
    
    // Sort
    if (sortOrder === 'asc') {
      result.sort((a, b) => parsePrice(a.price) - parsePrice(b.price));
    } else if (sortOrder === 'desc') {
      result.sort((a, b) => parsePrice(b.price) - parsePrice(a.price));
    }
    
    return result;
  }, [packages, selectedOperators, priceRange, selectedTransport, sortOrder]);

  // Reset filters handler
  const handleResetFilters = () => {
    setSelectedOperators([]);
    setSelectedTransport([]);
    setSortOrder('none');
    if (packages.length > 0) {
      const prices = packages.map(p => parsePrice(p.price)).filter(p => p > 0);
      if (prices.length > 0) {
        setPriceRange({
          min: Math.floor(Math.min(...prices)),
          max: Math.ceil(Math.max(...prices))
        });
      }
    }
  };

  // Loading state
  if (loadingGeo) {
    return (
      <div className="min-h-screen bg-navy pt-20 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-gold animate-spin" />
      </div>
    );
  }

  // Show packages for a specific country
  if (currentCountry) {
    return (
      <div className="min-h-screen bg-navy pt-20" data-testid="circuite-country-page">
        {/* Breadcrumb & Header */}
        <div className="bg-gradient-to-b from-navy-light to-navy py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm mb-4">
              <Link to="/circuite" className="text-white/60 hover:text-gold transition-colors">
                Circuite Externe
              </Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <Link to={`/circuite/${continent}`} className="text-white/60 hover:text-gold transition-colors">
                {currentContinent?.name}
              </Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <span className="text-gold">{currentCountry.name}</span>
            </div>
            
            {/* Back button */}
            <Link
              to={`/circuite/${continent}`}
              className="inline-flex items-center gap-2 text-white/60 hover:text-gold transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Înapoi la {currentContinent?.name}
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gold/20 flex items-center justify-center">
                <MapPin className="w-8 h-8 text-gold" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-3xl md:text-4xl text-white">
                  Circuite {currentCountry.name}
                </h1>
                <p className="text-white/60 mt-1">
                  {filteredPackages.length} circuite disponibile
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-12 h-12 text-gold animate-spin" />
            </div>
          ) : packages.length > 0 ? (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Filters - always show when there are packages in this country */}
              <div className="lg:w-72 flex-shrink-0">
                <FiltersPanel
                  packages={packages}
                  operators={operators}
                  selectedOperators={selectedOperators}
                  onOperatorChange={setSelectedOperators}
                  priceRange={priceRange}
                  onPriceChange={setPriceRange}
                  selectedTransport={selectedTransport}
                  onTransportChange={setSelectedTransport}
                  sortOrder={sortOrder}
                  onSortChange={setSortOrder}
                  onReset={handleResetFilters}
                />
              </div>
              
              {/* Packages Grid or Empty State */}
              <div className="flex-1">
                {filteredPackages.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredPackages.map((pkg) => (
                      <PackageCard key={pkg.id} package={pkg} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 glass rounded-2xl">
                    <p className="text-white/60 text-lg mb-2">
                      Nu există circuite care să corespundă filtrelor selectate.
                    </p>
                    <button
                      onClick={handleResetFilters}
                      className="text-gold hover:underline mt-2"
                    >
                      Resetează filtrele
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-white/60 text-lg">
                Nu există circuite disponibile pentru {currentCountry.name} momentan.
              </p>
              <Link
                to={`/circuite/${continent}`}
                className="inline-flex items-center gap-2 text-gold hover:underline mt-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Explorează alte țări din {currentContinent?.name}
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show countries for a specific continent
  if (currentContinent) {
    return (
      <div className="min-h-screen bg-navy pt-20" data-testid="circuite-continent-page">
        {/* Header */}
        <div className="bg-gradient-to-b from-navy-light to-navy py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm mb-4">
              <Link to="/circuite" className="text-white/60 hover:text-gold transition-colors">
                Circuite Externe
              </Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <span className="text-gold">{currentContinent.name}</span>
            </div>
            
            {/* Back button */}
            <Link
              to="/circuite"
              className="inline-flex items-center gap-2 text-white/60 hover:text-gold transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Toate continentele
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gold/20 flex items-center justify-center">
                <Globe className="w-8 h-8 text-gold" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-3xl md:text-4xl text-white">
                  {currentContinent.name}
                </h1>
                <p className="text-white/60 mt-1">
                  {currentContinent.description || `Explorează circuitele din ${currentContinent.name}`}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Countries Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {continentCountries.length > 0 ? (
            <>
              <h2 className="font-heading font-semibold text-xl text-white mb-6">
                Alege o țară ({continentCountries.length} disponibile)
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {continentCountries.map((c) => (
                  <CountryCard
                    key={c.id}
                    country={c}
                    continentSlug={continent}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-white/60 text-lg">
                Nu există țări configurate pentru {currentContinent.name} momentan.
              </p>
              <Link
                to="/circuite"
                className="inline-flex items-center gap-2 text-gold hover:underline mt-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Înapoi la continente
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show all continents
  return (
    <div className="min-h-screen bg-navy pt-20" data-testid="circuite-page">
      {/* Hero Header */}
      <div className="bg-gradient-to-b from-navy-light to-navy py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4">
            Circuite Externe
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Descoperă cele mai frumoase destinații din lume!
          </p>
        </div>
      </div>

      {/* Continents Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-heading font-semibold text-xl text-white mb-6">
          Alege un continent ({continents.length} disponibile)
        </h2>
        {continents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {continents.map((c) => (
              <ContinentCard key={c.id} continent={c} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-white/60 text-lg">
              Nu există continente configurate momentan.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
